import React from "react";
import { useTableAutoRefresh } from "../../hooks/useTableAutoRefresh";
import { MaterialReactTable } from "material-react-table";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import { sharedTableProps } from "../shared/tableProps";
import "../util/tables.css";
import { useDataView } from "../../contexts/data.view.context";
import ShowLoadingBar from "../util/showLoadingBar";
import {
  getGroupAccent,
  ToolbarStat,
  ToolbarLegend,
  ToolbarRow,
} from "../shared/monitoringStyles";

// EDS Events group borrows index 0 (primary/blue), Files Sent uses index 1
// (warning/amber) so the two metric groups are visually distinct instead of
// both being the same flat green. The two result groups use indexes 2 and 3.
const RECEIVED_GROUP_INDEX = 0;
const SENT_GROUP_INDEX = 1;
const RESULT_GROUP_INDEX = 2;
const ERRORS_GROUP_INDEX = 3;

// Parent header labels — used both in `columns` and for header background
// lookups, so they only need to be spelled once.
const HEADER_RECEIVED = "Files Recieved";
const HEADER_SENT = "Files Sent";
const HEADER_RESULT = "Last Transfer Result";
const HEADER_ERRORS = "Errors (30D)";

// Columns that start / end a metric group get the thick divider border.
const GROUP_START_IDS = new Set(["FilesReceived"]);
const GROUP_END_IDS = new Set([
  "FilesReceived_30D",
  "FilesSent_30D",
  "LastIngestDurationMs",
  "FailedTransfers_30D",
]);

// --- Formatting helpers ---

// The SQL columns are DATETIME2 in UTC with no offset, so the API usually
// returns them without a trailing "Z". Add one so the browser doesn't treat
// them as local time.
const toUtcDate = (value) => {
  if (!value) return null;
  const str = String(value);
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(str);
  const date = new Date(hasZone ? str : `${str}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = toUtcDate(value);
  if (!date) return "-";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
};

const isBlank = (value) => value === null || value === undefined || value === "";

const formatDuration = (ms) => {
  if (isBlank(ms)) return "-";
  const n = Number(ms);
  if (Number.isNaN(n)) return "-";
  return n < 1000 ? `${n} ms` : `${(n / 1000).toFixed(1)} s`;
};

// --- Cells ---

const ReturnCodeCell = ({ cell }) => {
  const code = cell.getValue();
  if (isBlank(code)) return "-";
  const ok = Number(code) >= 200 && Number(code) < 300;
  return (
    <Box
      component="span"
      sx={{ fontWeight: 700, color: ok ? "success.main" : "error.main" }}
    >
      {code}
    </Box>
  );
};

// Shows the number in red when it's above zero, so problems stand out.
const ErrorCountCell = ({ cell }) => {
  const value = cell.getValue();
  if (isBlank(value)) return "-";
  const n = Number(value);
  return (
    <Box
      component="span"
      sx={{
        fontWeight: n > 0 ? 700 : 400,
        color: n > 0 ? "error.main" : "text.primary",
      }}
    >
      {n}
    </Box>
  );
};

const TextOrDashCell = ({ cell }) => {
  const value = cell.getValue();
  return isBlank(value) ? "-" : value;
};

const columns = [
  {
    accessorKey: "PROJECT_ID",
    header: "Project ID",
    id: "PROJECT_ID",
    size: 100,
  },
  {
    accessorKey: "TRANSFER_CMD_ID",
    header: "Transfer Cmd ID",
    id: "TRANSFER_CMD_ID",
    size: 100,
    minSize: 100,
    maxSize: 100,
    enableResizing: false,
  },
  {
    accessorKey: "SOURCE",
    header: "Source",
    id: "SOURCE",
    size: 170,
  },
  {
    accessorKey: "DESTINATION",
    header: "Destination",
    id: "DESTINATION",
    size: 170,
  },
  {
    accessorKey: "FREQUENCY",
    header: "Frequency",
    id: "FREQUENCY",
    size: 100,
  },
  {
    accessorKey: "ExpectedNumberFiles",
    header: "Expected Number of Files",
    id: "ExpectedNumberFiles",
    size: 130,
  },

  {
    accessorKey: "LastTransferDate",
    header: "Last Transfer Date",
    id: "LastTransferDate",
    size: 170,
  },
  {
    accessorKey: "DaysSinceLastTransfer",
    header: "Days Since Last Transfer",
    id: "DaysSinceLastTransfer",
    size: 130,
  },
  /*
  {
    accessorKey: "SystemDataSteward",
    header: "System Data Steward",
    id: "SystemDataSteward",
    size: 50,
  },
  {
    accessorKey: "EmailDistributionList",
    header: "Email List",
    id: "EmailDistributionList",
    size: 50,
  },
  **/

  {
    // Parent Column: Recieved Files
    header: HEADER_RECEIVED,
    columns: [
      {
        header: "T",
        accessorKey: "FilesReceived",
        id: "FilesReceived",
        size: 60,
      },
      {
        header: "Y",
        accessorKey: "FilesReceived_Y",
        id: "FilesReceived_Y",
        size: 60,
      },
      {
        header: "7D",
        accessorKey: "FilesReceived_7D",
        id: "FilesReceived_7D",
        size: 65,
      },
      {
        header: "30D",
        accessorKey: "FilesReceived_30D",
        id: "FilesReceived_30D",
        size: 70,
      },
    ],
  },

  // Parent Column: Files Sent
  {
    header: HEADER_SENT,
    columns: [
      {
        header: "T",
        accessorKey: "FilesSent",
        id: "FilesSent",
        size: 60,
      },
      {
        header: "Y",
        accessorKey: "FilesSent_Y",
        id: "FilesSent_Y",
        size: 60,
      },
      {
        header: "7D",
        accessorKey: "FilesSent_7D",
        id: "FilesSent_7D",
        size: 65,
      },
      {
        header: "30D",
        accessorKey: "FilesSent_30D",
        id: "FilesSent_30D",
        size: 70,
      },
    ],
  },

  // Parent Column: result of the most recent transfer
  {
    header: HEADER_RESULT,
    columns: [
      {
        header: "Code",
        accessorKey: "LastReturnCode",
        id: "LastReturnCode",
        size: 75,
        Cell: ReturnCodeCell,
      },
      {
        header: "Description",
        accessorKey: "LastReturnDesc",
        id: "LastReturnDesc",
        size: 150,
        Cell: TextOrDashCell,
      },
      {
        header: "Errors",
        accessorKey: "LastResultErrorCount",
        id: "LastResultErrorCount",
        size: 75,
        Cell: ErrorCountCell,
      },
      {
        header: "Ingests",
        accessorKey: "LastIngestCount",
        id: "LastIngestCount",
        size: 80,
        Cell: TextOrDashCell,
      },
      {
        header: "Ingest Run ID",
        accessorKey: "LastIngestRunId",
        id: "LastIngestRunId",
        size: 110,
        Cell: TextOrDashCell,
      },
      {
        header: "Queued Filename",
        accessorKey: "LastQueuedFilename",
        id: "LastQueuedFilename",
        size: 260,
        Cell: TextOrDashCell,
      },
      {
        header: "Errored Files",
        accessorKey: "LastErroredFileCount",
        id: "LastErroredFileCount",
        size: 90,
        Cell: ErrorCountCell,
      },
      {
        header: "Ingest Start",
        accessorKey: "LastIngestStartTime",
        id: "LastIngestStartTime",
        size: 170,
        Cell: ({ cell }) => formatDateTime(cell.getValue()),
        sortingFn: "datetime",
      },
      {
        header: "Ingest End",
        accessorKey: "LastIngestEndTime",
        id: "LastIngestEndTime",
        size: 170,
        Cell: ({ cell }) => formatDateTime(cell.getValue()),
        sortingFn: "datetime",
      },
      {
        header: "Duration",
        accessorKey: "LastIngestDurationMs",
        id: "LastIngestDurationMs",
        size: 90,
        Cell: ({ cell }) => formatDuration(cell.getValue()),
      },
    ],
  },

  // Parent Column: 30-day error rollups
  {
    header: HEADER_ERRORS,
    columns: [
      {
        header: "Total Errors",
        accessorKey: "ResultErrors_30D",
        id: "ResultErrors_30D",
        size: 85,
        Cell: ErrorCountCell,
      },
      {
        header: "Failed Transfers",
        accessorKey: "FailedTransfers_30D",
        id: "FailedTransfers_30D",
        size: 90,
        Cell: ErrorCountCell,
      },
    ],
  },
];

/** 
const h2Style = {
  paddingLeft: "20px",
};
*/

const UnifiedFileSummary = () => {
  const theme = useTheme();
  const { fetchUnifiedFileSummary, unifiedFileSummary } = useDataView();
  let loading = false;
  const tableMaxHeight = useTableAutoRefresh(fetchUnifiedFileSummary, 300_000);

  if (unifiedFileSummary.length === 0) {
    loading = true;
  }

  const totalFilesToday = unifiedFileSummary.reduce(
    (sum, summaryEvent) => sum + (Number(summaryEvent.FilesReceived) || 0),
    0,
  );

  const allEntries = Array.from(unifiedFileSummary.values());
  const lastRefreshedTime = allEntries.reduce((latest, entry) => {
    if (!entry?.InsertDate) return latest;
    return !latest || new Date(entry.InsertDate) > new Date(latest)
      ? entry.InsertDate
      : latest;
  }, null);

  const localDateString = lastRefreshedTime
    ? new Date(lastRefreshedTime).toLocaleString("en-US", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeZoneName: "short",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "N/A";

  if (Boolean(loading)) {
    return (
      <ShowLoadingBar title="Unified Monitoring Summary" loading={loading} />
    );
  }

  return (
    <div className="relative">
      {/*
  <h2 style={{ textAlign: "center", color: "#0b4d8fff", ...h2Style }}>
    Unified Monitoring Summary
  </h2>
  */}

      {unifiedFileSummary.length > 0 && (
        <MaterialReactTable
          {...sharedTableProps}
          columns={columns}
          data={unifiedFileSummary.map((summaryEvent) => {
            return {
              TRANSFER_ID: summaryEvent.TRANSFER_ID,
              PROJECT_ID: summaryEvent.PROJECT_ID,
              TRANSFER_CMD_ID: summaryEvent.TRANSFER_CMD_ID,
              SOURCE: summaryEvent.SOURCE,
              DESTINATION: summaryEvent.DESTINATION,
              FREQUENCY: summaryEvent.FREQUENCY,
              SystemDataSteward: summaryEvent.SystemDataSteward,
              EmailDistributionList: summaryEvent.EmailDistributionList,
              LastTransferDate: summaryEvent.LastTransferDate,
              DaysSinceLastTransfer: summaryEvent.DaysSinceLastTransfer,
              ExpectedNumberFiles: summaryEvent.ExpectedNumberFiles,
              FilesReceived: summaryEvent.FilesReceived,
              FilesReceived_Y: summaryEvent.FilesReceived_Y,
              FilesReceived_7D: summaryEvent.FilesReceived_7D,
              FilesReceived_30D: summaryEvent.FilesReceived_30D,
              FilesSent: summaryEvent.FilesSent,
              FilesSent_Y: summaryEvent.FilesSent_Y,
              FilesSent_7D: summaryEvent.FilesSent_7D,
              FilesSent_30D: summaryEvent.FilesSent_30D,
              LastReturnCode: summaryEvent.LastReturnCode,
              LastReturnDesc: summaryEvent.LastReturnDesc,
              LastResultErrorCount: summaryEvent.LastResultErrorCount,
              LastIngestCount: summaryEvent.LastIngestCount,
              LastIngestRunId: summaryEvent.LastIngestRunId,
              LastQueuedFilename: summaryEvent.LastQueuedFilename,
              LastErroredFileCount: summaryEvent.LastErroredFileCount,
              LastIngestStartTime: summaryEvent.LastIngestStartTime,
              LastIngestEndTime: summaryEvent.LastIngestEndTime,
              LastIngestDurationMs: summaryEvent.LastIngestDurationMs,
              ResultErrors_30D: summaryEvent.ResultErrors_30D,
              FailedTransfers_30D: summaryEvent.FailedTransfers_30D,
            };
          })}
          enablePinning
          initialState={{
            density: "compact",
            pagination: { pageSize: 100 },
            sorting: [
              { id: "PROJECT_ID", desc: false }, // ✅ latest first
            ],
            columnPinning: {
              left: ["PROJECT_ID", "TRANSFER_CMD_ID"], // 👈 freeze first 2 columns
            },
          }}
          autoResetPageIndex={false}
          renderTopToolbarCustomActions={() => (
            <ToolbarRow>
              <ToolbarStat
                label="Last Refreshed"
                value={localDateString}
                color="primary.main"
              />
              <ToolbarStat
                label="Total Files Transferred Today"
                value={totalFilesToday}
                color="success.main"
              />
              <ToolbarLegend />
            </ToolbarRow>
          )}
          muiTopToolbarProps={{
            sx: {
              mt: 3, // margin-top for the whole top toolbar (icons + search + your custom labels)
            },
          }}
          muiTableBodyCellProps={({ cell, column }) => {
            const dividerBorder = `3px solid ${theme.palette.primary.main}`;
            return {
              sx: {
                borderBottom: "1px solid",
                borderColor: "divider",
                textAlign: cell.getValue() === "-" ? "center" : "left",
                padding: "6px 10px",
                fontSize: "0.8rem",
                // No column truncates its text — SOURCE/DESTINATION can run
                // long, and clipping them silently (with no way to see the
                // full value) is worse than a taller row. Every column wraps
                // onto multiple lines instead; short numeric/status columns
                // are unaffected since they never wrap in practice.
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
                // All group dividers share one accent color (theme
                // primary) — a leading border on the whole metric-group
                // cluster plus a trailing border after each group, rather
                // than a different color per junction.
                borderLeft: GROUP_START_IDS.has(column.id)
                  ? dividerBorder
                  : undefined,
                borderRight: GROUP_END_IDS.has(column.id)
                  ? dividerBorder
                  : undefined,
              },
            };
          }}
          muiTableHeadCellProps={({ column }) => {
            const dividerBorder = `3px solid ${theme.palette.primary.main}`;

            const groupIndexByHeader = {
              [HEADER_RECEIVED]: RECEIVED_GROUP_INDEX,
              [HEADER_SENT]: SENT_GROUP_INDEX,
              [HEADER_RESULT]: RESULT_GROUP_INDEX,
              [HEADER_ERRORS]: ERRORS_GROUP_INDEX,
            };
            const groupIndex = groupIndexByHeader[column.columnDef.header];
            const parentBg =
              groupIndex !== undefined
                ? getGroupAccent(theme, groupIndex).headerBg
                : undefined;

            return {
              sx: {
                backgroundColor: parentBg || "#F8FAFC",
                color: "text.secondary",
                fontWeight: 700,
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                textAlign: "center",
                borderBottom: "2px solid",
                borderColor: "divider",
                "& .Mui-TableHeadCell-Content": {
                  justifyContent: "center",
                },
                // MaterialReactTable forces white-space:nowrap on header
                // labels under 20 characters, truncating with an ellipsis
                // regardless of column width — force wrapping back on.
                "& .Mui-TableHeadCell-Content-Wrapper": {
                  whiteSpace: "normal",
                  overflow: "visible",
                },
                borderLeft: GROUP_START_IDS.has(column.id)
                  ? dividerBorder
                  : undefined,
                borderRight: GROUP_END_IDS.has(column.id)
                  ? dividerBorder
                  : undefined,
              },
            };
          }}
          muiTableBodyRowProps={{
            sx: {
              "&:nth-of-type(even) td": { backgroundColor: "#FAFBFC" },
              "&:hover td": { backgroundColor: "action.hover" },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: tableMaxHeight,
            },
          }}
          muiTableHeadProps={{
            sx: {
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "background.paper",
            },
          }}
        />
      )}
    </div>
  );
};

export default UnifiedFileSummary;